import { Box, CircularProgress, Paper, Typography } from '@mui/material'
import React from 'react'
import { IStockCard } from 'interfaces/IStockCard'
import { Alert } from '@mui/material';
import InventoryTable, { ColumnDef } from 'components/inventory/InventoryTable'
import { IIngredient } from 'interfaces/IIngredient'
import { formatStockQuantity } from 'components/inventory/helpers'
// Icons removed as they are not used in this component

const lastMonth = new Date()
lastMonth.setMonth(lastMonth.getMonth() - 1)

export default function StockCardIngredientHistory(props: {
  ingredientId: string
  data: IIngredient[]
  history: IStockCard[]
  loadHistory: (ingredientId: string) => void
  isLoading: boolean
  alertMessage: string
  searchingFor: string
  headCells: {
    id: string
    numeric: boolean
    disablePadding: boolean
    label: string
    width: string
    isChevron?: boolean
  }[]
  stockCardHeadCells?: {
    id: string
    numeric: boolean
    disablePadding: boolean
    label: string
    width: string
    isDate?: boolean
    isNewQuantity?: boolean
    isPartLevel?: boolean
  }[]
}) {
  const {
    ingredientId,
    data,
    isLoading,
    history,
    searchingFor,
    stockCardHeadCells = [],
  } = props
  const [selectedIngredientId] = React.useState(ingredientId)

  const getDisplayData = (
    headCell: any,
    stockCard: IStockCard,
    stockCardIngredient: any
  ) => {
    const dataToUse = headCell.isPartLevel ? stockCardIngredient : stockCard
    if (headCell.isDate) {
      return new Date(dataToUse[headCell.id] ?? '').toLocaleDateString()
    }
    if (headCell.isNewQuantity) {
      return formatStockQuantity(dataToUse.dynamicLatestStock ?? 0)
    }
    const value = dataToUse[headCell.id]
    // Format numeric quantity fields to 6 decimal places
    if (
      (headCell.id === 'confirmedQuantity' || headCell.id === 'quantity') &&
      value != null &&
      !isNaN(Number(value))
    ) {
      return formatStockQuantity(value)
    }
    // @ts-ignore
    return value
  }

  const flatten = (direction: string) => {
    const allRows: any[] = []
    const seen = new Set<string | number>()

    // First, collect all rows for this ingredient across ALL directions
    // to calculate the balance correctly working backwards.
    history?.forEach((stockCard) => {
      if (!stockCard) {
        return
      }
      stockCard.stockCardIngredients?.forEach((sci: any) => {
        if (!sci) {
          return
        }
        const sciId = sci.ingredientId || sci.ingredient?.ingredientId
        if (sciId !== selectedIngredientId || !sciId) {
          return
        }
        const uniqueKey =
          sci.stockCardIngredientId ?? `${stockCard.stockCardId}-${sciId}`
        if (seen.has(uniqueKey)) {
          return
        }
        seen.add(uniqueKey)
        allRows.push({ stockCard, stockCardIngredient: sci })
      })
    })

    // Sort by date descending (newest first)
    allRows.sort(
      (a, b) =>
        new Date(b.stockCard?.createdAt ?? 0).getTime() -
        new Date(a.stockCard?.createdAt ?? 0).getTime()
    )

    // Calculate balance backwards
    // Start with current stock from 'data' prop (which is IIngredient[])
    let currentBalance = data.find(i => i && i.ingredientId === selectedIngredientId)?.stock ?? 0

    allRows.forEach((row) => {
      row.stockCardIngredient.dynamicLatestStock = currentBalance
      const qty = Number(row.stockCardIngredient.confirmedQuantity ?? 0)
      if (row.stockCard.inOutDirection === 'IN') {
        currentBalance -= qty
      } else {
        currentBalance += qty
      }
    })

    return allRows.filter(row => row.stockCard.inOutDirection === direction)
  }

  const makeColumns = (headCells: any[]): ColumnDef<any>[] =>
    headCells
      .filter((hc) => hc.id !== 'latestStock')
      .map((hc) => ({
        id: hc.id || hc.label.replace(/\s+/g, '_'),
        label: hc.label,
        align: 'center',
        render: (row: any) =>
          getDisplayData(hc, row.stockCard, row.stockCardIngredient),
      }))
  return (
    <Box>
      {data.length === 0 && (
        <Alert severity="warning">Tidak ada permintaan konfirmasi stok: {`${searchingFor}`}</Alert>
      )}
      {data.length !== 0 && (
        <Paper>
          <Box p={2} pt={0}>
            {Boolean(history?.length) && (
              <React.Fragment>
                <Typography variant="h6">Sejarah Kartu Stok</Typography>
                <Box display="flex" style={{ gap: 16, marginTop: 16 }}>
                  <div style={{ flex: 1 }}>
                    <Typography variant="subtitle1" align="center" gutterBottom>
                      Masuk
                    </Typography>
                    <InventoryTable
                      columns={makeColumns(stockCardHeadCells)}
                      rows={flatten('IN')}
                      getRowKey={(r) =>
                        `${r.stockCard.stockCardDisplayId}-${r.stockCardIngredient.ingredientId ||
                        r.stockCardIngredient.ingredient?.ingredientId
                        }`
                      }
                      emptyMessage="Tidak ada data masuk"
                    />
                  </div>
                  {/* Vertical divider */}
                  <Box
                    aria-hidden
                    style={{
                      width: 1,
                      backgroundColor: 'rgba(0,0,0,0.12)',
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <Typography variant="subtitle1" align="center" gutterBottom>
                      Keluar
                    </Typography>
                    <InventoryTable
                      columns={makeColumns(stockCardHeadCells)}
                      rows={flatten('OUT')}
                      getRowKey={(r) =>
                        `${r.stockCard.stockCardDisplayId}-${r.stockCardIngredient.ingredientId ||
                        r.stockCardIngredient.ingredient?.ingredientId
                        }`
                      }
                      emptyMessage="Tidak ada data keluar"
                    />
                  </div>
                </Box>
              </React.Fragment>
            )}
            {!history?.length && !isLoading && (
              <Box mt={2}>
                <Typography variant="h6">Tidak ada sejarah stok.</Typography>
              </Box>
            )}
            {isLoading && (
              <Box p={3}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Paper>
      )}
      {isLoading && (
        <Box p={3}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  )
}
